import { ChangeEvent, FC, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { File as FileIcon } from "../../../../components/icons";
import {
  Button,
  ButtonVariant,
  TextAreaComponent,
  Typography,
  TypographyVariant,
} from "../../../../components/ui-kit";
import { LockGrey } from "../../../../components/icons";
import {
  NotificationTypes,
  useNotification,
} from "../../../../hooks/useNotification";
import api from "../../../../util/api";
import { getApiErrorMessage } from "../../../../util/apiErrorMessage";
import { CompanyNoteApiType } from "../../../../util/types";
import { useRefreshCompanyLogs } from "../hooks/useGetCompanyLogs";

const autoResizeTextarea = (el: HTMLTextAreaElement | null) => {
  if (!el) return;
  el.style.height = "auto";
  el.style.height = `${el.scrollHeight}px`;
};
const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "svg"];

const isImageUrl = (url: string) => {
  const ext = url.split("?")[0].split(".").pop()?.toLowerCase();
  return !!ext && IMAGE_EXTENSIONS.includes(ext);
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px 30px 30px;
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-radius: 10px;
  height: max-content;
  @media (max-width: 767px) {
    padding: 14px;
  }
`;

const TopLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 3px;
`;

const Footnote = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding-bottom: 12px;
  border-bottom: 1px solid #eeeeee;
`;

const NotesList = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 20px;
  gap: 20px;
`;

const NoteItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const NoteBottomLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const NoteActions = styled.div`
  display: flex;
  gap: 14px;
`;

const Buttons = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 14px;
  align-items: center;
`;

const DefaultBtn = styled.button`
  border: none;
  background-color: transparent;
  color: #027aff;
  font-family: "Montserrat", serif;
  font-weight: 500;
  font-size: 13px;
  padding: 0;
  cursor: pointer;
  :hover {
    text-decoration: underline;
  }
  :disabled {
    color: #adb5bd;
    cursor: default;
    text-decoration: none;
  }
`;

const CancelButton = styled(DefaultBtn)`
  color: #13273f;
`;

const DeleteBtn = styled(DefaultBtn)`
  color: #ef6355;
`;

const AttachmentsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const AttachmentThumb = styled.img`
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #eeeeee;
  cursor: pointer;
`;

const AttachmentFileThumb = styled.div`
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  border: 1px solid #eeeeee;
  background: #f4f7f9;
  cursor: pointer;
`;

const PendingFileChip = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  background: #f4f7f9;
  border-radius: 6px;
  font-size: 12px;
`;

const RemoveChipBtn = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  color: #ef6355;
  font-weight: 600;
`;

const ExistingAttachmentThumb = styled.div`
  position: relative;
  display: inline-flex;
`;

const RemoveAttachmentBtn = styled.button`
  position: absolute;
  top: -6px;
  right: -6px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: none;
  background: #ef6355;
  color: #fff;
  font-size: 12px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
`;

const ChooseFilesRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ChooseFilesButton = styled.button`
  border: 1px solid #dbe3eb;
  background: #f4f7f9;
  border-radius: 5px;
  padding: 7px 14px;
  font-family: Montserrat;
  font-size: 13px;
  font-weight: 500;
  color: #13273f;
  cursor: pointer;
  :hover {
    background: #eef2f5;
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const AutoGrowTextArea = styled(TextAreaComponent)`
  textarea {
    resize: none;
    overflow: hidden;
    min-height: 60px;
  }
`;

const NoFileText = styled.span`
  font-family: Montserrat;
  font-size: 13px;
  color: #8181a5;
`;

interface CompanyNotesTabProps {
  companyId: string;
}

interface EditNoteFormProps {
  note: CompanyNoteApiType;
  onSaved: () => void;
  onCancel: () => void;
  companyId: string;
}

const NoteFileInput: FC<{
  files: File[];
  setFiles: (files: File[]) => void;
}> = ({ files, setFiles }) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const picked = event.target.files ? Array.from(event.target.files) : [];
    setFiles([...files, ...picked]);
    event.target.value = "";
  };

  return (
    <div>
      <ChooseFilesRow>
        <ChooseFilesButton
          type="button"
          onClick={() => inputRef.current?.click()}
        >
          Choose files
        </ChooseFilesButton>
        {files.length === 0 && <NoFileText>No file chosen</NoFileText>}
      </ChooseFilesRow>
      <HiddenFileInput
        ref={inputRef}
        type="file"
        multiple
        onChange={onChange}
      />
      {files.length > 0 && (
        <AttachmentsRow style={{ marginTop: 8 }}>
          {files.map((file, index) => (
            <PendingFileChip key={`${file.name}-${index}`}>
              {file.name}
              <RemoveChipBtn
                type="button"
                onClick={() =>
                  setFiles(files.filter((_, fileIndex) => fileIndex !== index))
                }
              >
                ×
              </RemoveChipBtn>
            </PendingFileChip>
          ))}
        </AttachmentsRow>
      )}
    </div>
  );
};

const EditNoteForm: FC<EditNoteFormProps> = ({
  note,
  onSaved,
  onCancel,
  companyId,
}) => {
  const [text, setText] = useState(note.text ?? "");
  const [files, setFiles] = useState<File[]>([]);
  const [removedAttachmentIds, setRemovedAttachmentIds] = useState<string[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const { showNotification } = useNotification();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    autoResizeTextarea(containerRef.current?.querySelector("textarea") ?? null);
  }, []);

  const handleTextChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setText(event.target.value);
    autoResizeTextarea(event.target);
  };

  const remainingAttachments = (note.imageAttachments ?? []).filter(
    (attachment) => !removedAttachmentIds.includes(attachment._id)
  );

  const handleSave = async () => {
    setLoading(true);
    try {
      await api.editCompanyNote(
        companyId,
        note._id,
        text,
        files,
        removedAttachmentIds
      );
      showNotification("Note has been updated", NotificationTypes.SUCCESS);
      onSaved();
    } catch (e: unknown) {
      showNotification(
        getApiErrorMessage(e, "Failed to update note"),
        NotificationTypes.WARNING
      );
    }
    setLoading(false);
  };

  return (
    <NoteItem ref={containerRef}>
      <AutoGrowTextArea
        value={text}
        onChange={handleTextChange}
        placeholder="Write a note..."
      />
      {remainingAttachments.length > 0 && (
        <AttachmentsRow>
          {remainingAttachments.map((attachment) => (
            <ExistingAttachmentThumb key={attachment._id}>
              {isImageUrl(attachment.url) ? (
                <AttachmentThumb
                  src={attachment.url}
                  alt="attachment"
                  onClick={() => window.open(attachment.url, "_blank")}
                />
              ) : (
                <AttachmentFileThumb
                  onClick={() => window.open(attachment.url, "_blank")}
                >
                  <FileIcon />
                </AttachmentFileThumb>
              )}
              <RemoveAttachmentBtn
                type="button"
                onClick={() =>
                  setRemovedAttachmentIds((prev) => [...prev, attachment._id])
                }
              >
                ×
              </RemoveAttachmentBtn>
            </ExistingAttachmentThumb>
          ))}
        </AttachmentsRow>
      )}
      <NoteFileInput files={files} setFiles={setFiles} />
      <Buttons>
        <Button onClick={handleSave} loading={loading} disabled={!text.length}>
          Save
        </Button>
        <CancelButton onClick={onCancel}>Cancel</CancelButton>
      </Buttons>
    </NoteItem>
  );
};

const NoteView: FC<{
  note: CompanyNoteApiType;
  companyId: string;
  refresh: () => void;
}> = ({ note, companyId, refresh }) => {
  const [isEdit, setIsEdit] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { showNotification } = useNotification();

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.deleteCompanyNote(companyId, note._id);
      showNotification("Note has been deleted", NotificationTypes.SUCCESS);
      refresh();
    } catch (e: unknown) {
      showNotification(
        getApiErrorMessage(e, "Failed to delete note"),
        NotificationTypes.WARNING
      );
      setDeleting(false);
    }
  };

  if (isEdit) {
    return (
      <EditNoteForm
        note={note}
        companyId={companyId}
        onCancel={() => setIsEdit(false)}
        onSaved={() => {
          setIsEdit(false);
          refresh();
        }}
      />
    );
  }

  return (
    <NoteItem>
      <Typography variant={TypographyVariant.BODY3}>{note.text}</Typography>
      {note.imageAttachments?.length > 0 && (
        <AttachmentsRow>
          {note.imageAttachments.map((attachment) =>
            isImageUrl(attachment.url) ? (
              <AttachmentThumb
                key={attachment._id}
                src={attachment.url}
                alt="attachment"
                onClick={() => window.open(attachment.url, "_blank")}
              />
            ) : (
              <AttachmentFileThumb
                key={attachment._id}
                onClick={() => window.open(attachment.url, "_blank")}
              >
                <FileIcon />
              </AttachmentFileThumb>
            )
          )}
        </AttachmentsRow>
      )}
      <NoteBottomLine>
        <Typography color="#495B6C" variant={TypographyVariant.BODY7}>
          {new Date(note.updatedAt ?? note.createdAt).toLocaleString()}
        </Typography>
        <NoteActions>
          <DefaultBtn onClick={() => setIsEdit(true)} disabled={deleting}>
            Edit
          </DefaultBtn>
          <DeleteBtn onClick={handleDelete} disabled={deleting}>
            Delete
          </DeleteBtn>
        </NoteActions>
      </NoteBottomLine>
    </NoteItem>
  );
};

export const CompanyNotesTab: FC<CompanyNotesTabProps> = ({ companyId }) => {
  const [notes, setNotes] = useState<CompanyNoteApiType[]>([]);
  const [showAddNote, setShowAddNote] = useState(false);
  const [newNoteText, setNewNoteText] = useState("");
  const [newNoteFiles, setNewNoteFiles] = useState<File[]>([]);
  const [savingNote, setSavingNote] = useState(false);
  const refreshLogs = useRefreshCompanyLogs(companyId);
  const { showNotification } = useNotification();

  const refresh = async () => {
    try {
      const result = await api.getCompanyNotes(companyId);
      setNotes(result);
    } catch (e: unknown) {
      showNotification(
        getApiErrorMessage(e, "Failed to load notes"),
        NotificationTypes.WARNING
      );
    }
  };

  // Note mutations are audited, so the activity log below the notes has a
  // new entry to show as well.
  const onNoteChanged = () => {
    refresh();
    refreshLogs();
  };

  useEffect(() => {
    if (companyId) refresh();
  }, [companyId]);

  const handleCancelAdd = () => {
    setShowAddNote(false);
    setNewNoteText("");
    setNewNoteFiles([]);
  };

  const handleSaveNewNote = async () => {
    setSavingNote(true);
    try {
      await api.createCompanyNote(companyId, newNoteText, newNoteFiles);
      showNotification("Note has been added", NotificationTypes.SUCCESS);
      handleCancelAdd();
      onNoteChanged();
    } catch (e: unknown) {
      showNotification(
        getApiErrorMessage(e, "Failed to add note"),
        NotificationTypes.WARNING
      );
    }
    setSavingNote(false);
  };

  return (
    <Container>
      <TopLine>
        <Typography variant={TypographyVariant.HEADER2}>Notes</Typography>
        {!showAddNote && (
          <DefaultBtn onClick={() => setShowAddNote(true)}>Add note</DefaultBtn>
        )}
      </TopLine>
      <Footnote>
        <LockGrey />
        <Typography color="#6C6C8A" variant={TypographyVariant.BODY7}>
          Notes are only visible in the Admin Portal
        </Typography>
      </Footnote>

      {showAddNote && (
        <NoteItem style={{ marginTop: 20 }}>
          <AutoGrowTextArea
            value={newNoteText}
            onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
              setNewNoteText(event.target.value);
              autoResizeTextarea(event.target);
            }}
            placeholder="Write a note..."
          />
          <NoteFileInput files={newNoteFiles} setFiles={setNewNoteFiles} />
          <Buttons>
            <Button
              onClick={handleSaveNewNote}
              loading={savingNote}
              disabled={!newNoteText.length}
            >
              Save
            </Button>
            <CancelButton onClick={handleCancelAdd}>Cancel</CancelButton>
          </Buttons>
        </NoteItem>
      )}

      <NotesList>
        {notes.length ? (
          notes.map((note) => (
            <NoteView
              key={note._id}
              note={note}
              companyId={companyId}
              refresh={onNoteChanged}
            />
          ))
        ) : (
          <Typography variant={TypographyVariant.BODY3}>
            There are no notes for this company
          </Typography>
        )}
      </NotesList>
    </Container>
  );
};
